import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Search } from 'react-bootstrap-icons';
import QRCode from 'react-qr-code';
import Cookies from 'js-cookie';

/**
 * Donor Search Modal Component
 * Provides a searchable interface to find and select donors
 * Similar to client search but for donor selection
 */
export function DonorSearchModal({
  show,
  onHide,
  selectedOrgID,
  onSelectDonor,
  donors = []
}) {
  const [showManualSearch, setShowManualSearch] = useState(false);
  const [searchFormData, setSearchFormData] = useState({
    firstName: "",
    lastName: "",
    organizationName: ""
  });
  const [filteredDonors, setFilteredDonors] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [donorScanCode, setDonorScanCode] = useState("");
  const [qrValue, setQrValue] = useState(Cookies.get('userID') || "");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  const codeInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setShowManualSearch(false);
      setSearchFormData({
        firstName: "",
        lastName: "",
        organizationName: ""
      });
      setDonorScanCode("");
    }
  }, [show]);

  // Focus appropriate input when switching modes
  useEffect(() => {
    if (show && showManualSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (show && !showManualSearch && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [show, showManualSearch]);

  // Filter donors based on search criteria whenever form data or donors change
  useEffect(() => {
    if (show && showManualSearch) {
      // If no search criteria, show empty list
      const hasSearchCriteria = searchFormData.firstName || searchFormData.lastName || searchFormData.organizationName;
      
      if (!hasSearchCriteria) {
        setFilteredDonors([]);
        return;
      }

      const results = donors.filter(donor => {
        const searchFirstName = searchFormData.firstName.toLowerCase();
        const searchLastName = searchFormData.lastName.toLowerCase();
        const searchOrgName = searchFormData.organizationName.toLowerCase();

        // Get donor display name
        const displayName = getDonorDisplayName(donor).toLowerCase();
        
        // Check if any search criteria matches the display name
        if (searchOrgName && displayName.includes(searchOrgName)) {
          return true;
        }

        // Check individual name matches
        const donorFirstName = (donor.clientFirstName || donor.volunteerFirstName || "").toLowerCase();
        const donorLastName = (donor.clientLastName || donor.volunteerLastName || "").toLowerCase();
        const donorOrgName = (donor.organizationName || "").toLowerCase();
        const donorPhone = (donor.phoneNumber || "").replace(/\D/g, ""); // Remove non-digits from phone

        // Remove non-digits from search term for phone matching
        const searchPhone = searchOrgName.replace(/\D/g, "");

        if (searchFirstName && donorFirstName.includes(searchFirstName)) {
          return true;
        }
        if (searchLastName && donorLastName.includes(searchLastName)) {
          return true;
        }
        if (searchOrgName && donorOrgName.includes(searchOrgName)) {
          return true;
        }
        // Check phone number match (digits only)
        if (searchPhone && donorPhone.includes(searchPhone)) {
          return true;
        }

        return false;
      });

      setFilteredDonors(results);
      setSelectedResult(null);
    }
  }, [show, showManualSearch, searchFormData, donors]);

  const handleSearchChange = (e) => {
    setSearchFormData({
      ...searchFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleClearSearch = () => {
    setSearchFormData({
      firstName: "",
      lastName: "",
      organizationName: ""
    });
  };

  const handleSelectDonor = (donor) => {
    setSelectedResult(donor);
    onSelectDonor(donor);
    onHide();
    // Reset form
    setSearchFormData({
      firstName: "",
      lastName: "",
      organizationName: ""
    });
  };

  const getDonorDisplayName = (donor) => {
    if (donor.organizationName) {
      return donor.organizationName;
    } else if (donor.clientFirstName) {
      return `${donor.clientFirstName} ${donor.clientLastName || ""}`.trim();
    } else if (donor.volunteerFirstName) {
      return `${donor.volunteerFirstName} ${donor.volunteerLastName || ""}`.trim();
    }
    return donor.donorDisplayName || `Donor ${donor.donorID}`;
  };

  const getDonorType = (donor) => {
    if (donor.organizationName) return "Organization";
    if (donor.volunteerFirstName) return "Volunteer";
    if (donor.clientFirstName) return "Individual";
    return "Donor";
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="min-size-modal">
      <div className="text-dark">
        <Modal.Header>
          <div className="d-flex justify-content-center w-100 position-relative">
            {/* Back button when in manual search mode */}
            {showManualSearch && (
              <Button
                variant="light"
                onClick={() => setShowManualSearch(false)}
                className="position-absolute top-0 start-0 ms-2 mt-2"
                style={{ zIndex: 2 }}
                aria-label="Back"
              >
                <i className="fa fa-arrow-left"></i>
              </Button>
            )}
            <Modal.Title>
              <Search className="me-2" />
              Search Donors
            </Modal.Title>
            <Button
              variant="light"
              onClick={onHide}
              className="position-absolute top-0 end-0"
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Debug info */}
          {donors.length === 0 && (
            <div className="alert alert-warning mb-3">
              <strong>No donors available.</strong> Make sure donors exist in the system before receiving donations.
            </div>
          )}

          {/* QR Code and Search Selection Screen */}
          {!showManualSearch ? (
            <>
              <div
                className={
                  windowWidth < 1000
                    ? "d-flex flex-column align-items-center justify-content-center w-100"
                    : "d-flex flex-row align-items-center justify-content-center w-100"
                }
                style={{ minHeight: 350 }}
              >
                {/* QR Code Card */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <h5 className="text-center mb-3">QR Code</h5>
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "30px 20px",
                      borderRadius: "14px",
                      border: "1px solid #ced4da",
                      width: "240px",
                      minHeight: "240px",
                      minWidth: "160px",
                      background: "#f8f9fa",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative"
                    }}
                  >
                    <QRCode value={qrValue || "No QR Code Generated"} viewBox="0 0 256 256" size={172} />
                  </div>
                  <div className="text-muted mt-3" style={{ fontSize: "0.95em" }}>
                    Let donor scan to donate
                  </div>
                </div>

                {/* Divider with OR */}
                <div
                  className="d-flex flex-column align-items-center mx-5"
                  style={{ justifyContent: "center" }}
                >
                  <div style={{
                    background: "#fff",
                    border: "1px solid #ced4da",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "8px 0"
                  }}>
                    <span style={{ fontWeight: "550", fontSize: "1.1em" }}>or</span>
                  </div>
                </div>

                {/* Manual Search Card */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <h5 className="text-center mb-3">Search</h5>
                  <div
                    style={{
                      padding: "18px 10px",
                      borderRadius: "10px",
                      border: "1px solid #ced4da",
                      width: "240px",
                      minHeight: "240px",
                      minWidth: "240px",
                      background: "#f8f9fa",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                    onClick={() => setShowManualSearch(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e9ecef";
                      e.currentTarget.style.borderColor = "#adb5bd";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f8f9fa";
                      e.currentTarget.style.borderColor = "#ced4da";
                    }}
                  >
                    <i className="bi bi-search mb-2 text-primary" style={{ fontSize: '5em' }}></i>
                  </div>
                  <div className="text-muted mt-3" style={{ fontSize: "0.95em" }}>
                    Search by name or organization
                  </div>
                </div>
              </div>

              {/* Scan input at bottom */}
              <div className="d-flex justify-content-center mt-4" style={{ maxWidth: 400, margin: "0 auto" }}>
                <Form.Control
                  type="password"
                  placeholder="Scan or enter donor code here"
                  value={donorScanCode}
                  onChange={e => setDonorScanCode(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && donorScanCode.trim()) {
                      // Search by userID (from QR code scan)
                      let matchedDonor = null;
                      
                      // Check if scanned code matches any donor's userID
                      // For clients: userID is stored separately
                      // For volunteers: userID is stored separately
                      // For organizations: userID is the org's user reference
                      matchedDonor = donors.find(d => {
                        // Try to match against client userID
                        if (d.clientID && d.userID === donorScanCode) return true;
                        // Try to match against volunteer userID
                        if (d.volunteerID && d.userID === donorScanCode) return true;
                        // Try to match against org userID
                        if (d.orgID && d.userID === donorScanCode) return true;
                        // Fallback to donorID match
                        if (String(d.donorID) === donorScanCode) return true;
                        return false;
                      });
                      
                      if (!matchedDonor) {
                        alert("Donor not found. Please check the code or register the donor first.");
                        setDonorScanCode("");
                        return;
                      }
                      handleSelectDonor(matchedDonor);
                      setDonorScanCode("");
                      e.preventDefault();
                    }
                  }}
                  ref={codeInputRef}
                  style={{ padding: "10px", fontSize: "1.1em" }}
                  aria-label="Scan or enter donor code here"
                />
              </div>
            </>
          ) : (
            /* Manual Search Screen */
            <div className="d-flex flex-column align-items-center mt-4">
              <div className="d-flex mb-3 align-items-center" style={{ maxWidth: 400, width: "100%" }}>
                <Form className="flex-grow-1">
                  <Form.Control
                    type="search"
                    placeholder="Search by name, phone, or organization..."
                    aria-label="Search"
                    value={searchFormData.organizationName}
                    onChange={(e) => {
                      setSearchFormData({
                        firstName: e.target.value,
                        lastName: e.target.value,
                        organizationName: e.target.value
                      });
                    }}
                    ref={searchInputRef}
                  />
                </Form>
              </div>

              {/* Search Results */}
              <div className='flex-column' style={{ maxWidth: 400, marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                {searchFormData.organizationName ? (
                  filteredDonors.length > 0 ? (
                    <ul className="list-group flex-grow-1" style={{ maxWidth: 400, width: "100%" }}>
                      {filteredDonors.slice(0, 8).map((donor) => (
                        <li
                          key={donor.donorID}
                          className="list-group-item"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSelectDonor(donor)}
                        >
                          <strong>{getDonorDisplayName(donor)}</strong>
                          <br />
                          <small className="text-muted">Type: {getDonorType(donor)}</small>
                          {donor.yearToDateDonationValue > 0 && (
                            <>
                              <br />
                              <small className="text-success">
                                YTD Donations: ${donor.yearToDateDonationValue.toFixed(2)}
                              </small>
                            </>
                          )}
                        </li>
                      ))}
                      {/* Fill empty rows to maintain consistent height */}
                      {Array.from({ length: 8 - Math.min(8, filteredDonors.length) }).map((_, idx) => (
                        <li
                          key={`empty-${idx}`}
                          className="list-group-item"
                          style={{ height: 64.64, background: "transparent", border: "transparent" }}
                        />
                      ))}
                    </ul>
                  ) : (
                    <div className="alert alert-info mb-0">
                      <strong>No donors found.</strong> Try adjusting your search criteria.
                    </div>
                  )
                ) : (
                  <div className="alert alert-info mb-0">
                    <strong>Start typing</strong> to search for donors.
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
}

export default DonorSearchModal;
